import React, { useState } from "react";
import assets from "../../assets";
import styles from "./index.module.scss";
import ProjectCard from "./ProjectCard";
import PaginationDot from "../PaginationDot";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loading from "../Loading";
import Checkbox from "@mui/material/Checkbox";
import { FormControlLabel } from "@mui/material";

export default function ProjectsFilters({
  filters,
  activeFilters,
  projects,
  loadingProducts,
  pageNum,
  setActiveFilters,
}) {
  const [showFilters, setShowFilters] = useState(false);
  const [tempFilters, setTempFilters] = useState({ area: [], service: [] });
  const setProjectType = (type) => {
    setActiveFilters((prev) => {
      return { ...prev, type };
    });
  };
  const { locale } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleServiceChange = (id) => {
    if (tempFilters.service.includes(id)) {
      setTempFilters((prev) => {
        return {
          ...prev,
          service: prev.service.filter((service) => service !== id),
        };
      });
    } else {
      setTempFilters((prev) => {
        return { ...prev, service: [...prev.service, id] };
      });
    }
  };

  const handleAreaChange = (id) => {
    if (tempFilters.area.includes(id)) {
      setTempFilters((prev) => {
        return { ...prev, area: prev.area.filter((area) => area !== id) };
      });
    } else {
      setTempFilters((prev) => {
        return { ...prev, area: [...prev.area, id] };
      });
    }
  };

  const resetFilters = () => {
    setTempFilters({ area: [], service: [] });
    setActiveFilters((prev) => {
      return { ...prev, area: [], service: [] };
    });
    setShowFilters(false);
  };

  const cancelFilters = () => {
    setTempFilters({ area: [], service: [] });
    setShowFilters(false);
  };

  const applyFilters = () => {
    setActiveFilters((prev) => {
      return { ...prev, ...tempFilters };
    });
    setShowFilters(false);
  };

  const prevPage = () => {
    navigate(`/${locale}/projects?page_num=${pageNum - 1}`);
  };
  const nextPage = () => {
    navigate(`/${locale}/projects?page_num=${pageNum + 1}`);
  };

  return (
    <div className={styles.projectsFilters}>
      <div className={styles.filtersNav}>
        <div className={styles.categories}>
          <span
            onClick={() => {
              setProjectType("all");
              navigate(`/${locale}/projects?page_num=1`);
            }}
            className={activeFilters?.type === "all" ? styles.activeType : ""}
          >
            {t("projects.showall")}
          </span>
          {filters?.project_type.map((type) => {
            return (
              <span
                onClick={() => {
                  setProjectType(type.id);
                  navigate(`/${locale}/projects?page_num=1`);
                }}
                className={
                  activeFilters?.type === type.id ? styles.activeType : ""
                }
                key={type.id}
              >
                {type.title}
              </span>
            );
          })}
        </div>
        <button onClick={() => setShowFilters((prev) => !prev)}>
          <img
            src={assets.icons.filterIcon}
            alt="Filter"
            width={22}
            height={16}
          />
          {t("filter")}
        </button>
      </div>
      <div
        className={`${styles.filters} ${showFilters ? styles.filtersOpen : ""}`}
      >
        <div className={styles.filtersInner}>
          <div className={styles.filtersInnerHeader}>
            <h3 className={styles.filtersInnerTitle}>{t("filter")}</h3>
          </div>
          <div className={styles.filtersServices}>
            <h4 className={styles.filtersServicesTitle}>
              {t("filter_by_service")}
            </h4>
            {filters?.project_service.map((service) => {
              return (
                <FormControlLabel
                  key={service.id}
                  label={service.title}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    "& .MuiSvgIcon-root": {
                      width: "3rem",
                      height: "3rem",
                      color: "#133A25",
                    },
                    "& span": { fontSize: "1.8rem", fontWeight: 500 },
                    "& MuiButtonBase-root-MuiCheckbox-root.Mui-checked": {
                      color: "#133A25",
                    },
                  }}
                  control={
                    <Checkbox
                      sx={{ color: "#133A25" }}
                      checked={tempFilters?.service.includes(service.id)}
                      onChange={() => handleServiceChange(service.id)}
                    />
                  }
                />
              );
            })}
          </div>
          <div className={styles.filtersArea}>
            <h4 className={styles.filtersAreaTitle}>{t("filter_by_area")}</h4>
            {filters?.project_area.map((area) => {
              return (
                <FormControlLabel
                  key={area.id}
                  label={area.title}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    "& .MuiSvgIcon-root": {
                      width: "3rem",
                      height: "3rem",
                      color: "#133A25",
                    },
                    "& span": { fontSize: "1.8rem", fontWeight: 500 },
                    "& MuiButtonBase-root-MuiCheckbox-root.Mui-checked": {
                      color: "#133A25",
                    },
                  }}
                  control={
                    <Checkbox
                      sx={{ color: "#133A25" }}
                      checked={tempFilters?.area.includes(area.id)}
                      onChange={() => handleAreaChange(area.id)}
                    />
                  }
                />
              );
            })}
          </div>
          <div className={styles.filtersActions}>
            <button
              onClick={() => {
                if (
                  activeFilters.service.length > 0 ||
                  activeFilters.area.length > 0
                ) {
                  resetFilters();
                } else {
                  cancelFilters();
                }
              }}
              className={styles.filtersActionsCancel}
            >
              {activeFilters.service.length > 0 || activeFilters.area.length > 0
                ? t("reset")
                : t("cancel")}
            </button>
            <button
              onClick={applyFilters}
              className={styles.filtersActionsSubmit}
            >
              {t("apply")}
            </button>
          </div>
        </div>
      </div>

      {loadingProducts && (
        <div className={styles.loading}>
          <Loading />
        </div>
      )}
      {!loadingProducts && projects?.results.results.length > 0 && (
        <>
          <div className={styles.projects}>
            {projects?.results?.results.map((project) => {
              return <ProjectCard key={project.id} project={project} />;
            })}
          </div>
          <div className={styles.pagination}>
            <button
              disabled={pageNum === 1}
              className={styles.paginationPrev}
              onClick={prevPage}
            >
              <img src={assets.icons.prevArrow} alt="previous arrow" />
            </button>
            {new Array(Math.ceil(projects?.count / 9) || 1)
              .fill(0)
              .map((page, index) => (
                <PaginationDot
                  key={index}
                  active={
                    Number(searchParams.get("page_num"))
                      ? Number(searchParams.get("page_num")) === index + 1
                      : Number(searchParams.get("page_num")) === index
                  }
                  pageNum={index + 1}
                />
              ))}
            <button
              onClick={nextPage}
              disabled={pageNum === Math.ceil(projects?.count / 9)}
              className={styles.paginationNext}
            >
              <img src={assets.icons.nextArrow} alt="next arrow" />
            </button>
          </div>
        </>
      )}
      {!loadingProducts && projects?.results.results.length === 0 && (
        <p className={styles.noProducts}>{t("noProducts")}</p>
      )}
    </div>
  );
}
